import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../context/StoreContext';
import { invoicesAPI, bookingsAPI, extractErrorMessage } from '../api';

const STATUS_TABS = ['all', 'pending', 'partially_paid', 'paid'];

const Invoices = () => {
  const { selectedStore } = useStore();
  const [invoices, setInvoices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [formData, setFormData] = useState({ booking_id: '', customer_id: '' });

  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (activeTab !== 'all') params.status = activeTab;
      const response = await invoicesAPI.list(selectedStore.id, params);
      setInvoices(Array.isArray(response.data) ? response.data : response.data.items || []);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load invoices'));
    } finally {
      setLoading(false);
    }
  }, [selectedStore, activeTab]);

  const loadBookings = useCallback(async () => {
    try {
      const response = await bookingsAPI.list(selectedStore.id, { status: 'active' });
      setBookings(Array.isArray(response.data) ? response.data : response.data.items || []);
    } catch (err) { /* optional */ }
  }, [selectedStore]);

  useEffect(() => {
    if (selectedStore) {
      loadInvoices();
      loadBookings();
    }
  }, [selectedStore, loadInvoices, loadBookings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, store_id: selectedStore.id };
      if (editingId) {
        await invoicesAPI.update(editingId, data);
      } else {
        await invoicesAPI.create(data);
      }
      loadInvoices();
      resetForm();
    } catch (err) {
      setError(extractErrorMessage(err, 'Operation failed'));
    }
  };

  const handleDownload = async (id) => {
    try {
      const response = await invoicesAPI.download(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to download invoice'));
    }
  };

  const handleWhatsApp = async (id) => {
    try {
      const response = await invoicesAPI.getWhatsAppLink(id);
      if (response.data?.url || response.data?.whatsapp_url) {
        window.open(response.data.url || response.data.whatsapp_url, '_blank');
      }
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to get WhatsApp link'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this invoice?')) return;
    try {
      await invoicesAPI.delete(id);
      loadInvoices();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to delete invoice'));
    }
  };

  const resetForm = () => {
    setFormData({ booking_id: '', customer_id: '' });
    setEditingId(null);
    setShowForm(false);
    setError(null);
  };

  const getStatusBadge = (status) => {
    const map = { pending: 'badge-warning', partially_paid: 'badge-info', paid: 'badge-success', overdue: 'badge-danger' };
    return map[status] || 'badge-info';
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div><span>Loading invoices...</span></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Invoices</h1>
        <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); if (showForm) resetForm(); }}>
          {showForm ? 'Cancel' : '+ Create Invoice'}
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="tab-bar">
        {STATUS_TABS.map(tab => (
          <button key={tab} className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}>
            {tab === 'partially_paid' ? 'Partially Paid' : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header">
            <h3 className="card-title">{editingId ? 'Edit Invoice' : 'Create Invoice'}</h3>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Booking *</label>
                  <select className="form-select" value={formData.booking_id}
                    onChange={(e) => {
                      const bookingId = parseInt(e.target.value);
                      const booking = bookings.find(b => b.id === bookingId);
                      setFormData({ ...formData, booking_id: bookingId, customer_id: booking?.customer_id || '' });
                    }} required>
                    <option value="">Select Booking</option>
                    {bookings.map(b => (
                      <option key={b.id} value={b.id}>
                        #{b.booking_number || b.id} - {b.customer_name || b.customer?.name || 'Customer'}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">{editingId ? 'Update' : 'Create Invoice'}</button>
                {editingId && <button type="button" className="btn btn-secondary" onClick={resetForm}>Cancel</button>}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mobile Cards View */}
      <div className="mobile-cards hidden-desktop">
        {invoices.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📄</div>
            <h3 className="empty-state-title">No invoices found</h3>
          </div>
        ) : (
          invoices.map(invoice => (
            <div className="mobile-card" key={invoice.id}>
              <div className="mobile-card-header">
                <div>
                  <div className="mobile-card-title">#{invoice.invoice_number || invoice.id}</div>
                  <div className="text-muted" style={{ fontSize: '0.8rem' }}>{invoice.customer_name || '-'}</div>
                </div>
                <span className={`badge ${getStatusBadge(invoice.status)}`}>{invoice.status}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Total</span>
                <span className="font-bold">${parseFloat(invoice.total_amount || 0).toFixed(2)}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Paid</span>
                <span>${parseFloat(invoice.paid_amount || 0).toFixed(2)}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Balance</span>
                <span>${parseFloat(invoice.balance || 0).toFixed(2)}</span>
              </div>
              <div className="mobile-card-actions">
                <button className="btn btn-sm btn-secondary" onClick={() => handleDownload(invoice.id)}>PDF</button>
                <button className="btn btn-sm btn-secondary" onClick={() => handleWhatsApp(invoice.id)}>WhatsApp</button>
                <button className="btn btn-sm btn-danger" onClick={() => handleDelete(invoice.id)}>Delete</button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="card desktop-table">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Invoice #</th><th>Booking</th><th>Customer</th><th>Total</th><th>Paid</th><th>Balance</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(invoice => (
                <tr key={invoice.id}>
                  <td className="font-medium">#{invoice.invoice_number || invoice.id}</td>
                  <td>#{invoice.booking_number || invoice.booking_id}</td>
                  <td>{invoice.customer_name || '-'}</td>
                  <td>${parseFloat(invoice.total_amount || 0).toFixed(2)}</td>
                  <td>${parseFloat(invoice.paid_amount || 0).toFixed(2)}</td>
                  <td className="font-bold">${parseFloat(invoice.balance || 0).toFixed(2)}</td>
                  <td><span className={`badge ${getStatusBadge(invoice.status)}`}>{invoice.status}</span></td>
                  <td>
                    <div className="flex gap-2">
                      <button className="btn btn-sm btn-secondary" onClick={() => handleDownload(invoice.id)}>PDF</button>
                      <button className="btn btn-sm btn-secondary" onClick={() => handleWhatsApp(invoice.id)}>WhatsApp</button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(invoice.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
              {invoices.length === 0 && (
                <tr><td colSpan="8" className="text-center text-muted" style={{ padding: '3rem' }}>No invoices found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Invoices;
